import {_} from 'lodash';
import * as ko from 'knockout';
import {ISystem, ISystemRole} from "../../index";
import {HIERARCHY_ARROW, MAX_RESULTS_PER_PAGE, Permission, SYSTEM_MEGA_EADM} from "../../constants";
import {getRoleName} from "../../util/misc";
import {global, lm} from "../../appContext";
import {getSearchParamsFn} from "../../util/request";
import {nsTooltip} from "../../util/nsTooltip";
import {IComboboxV2Options} from "./v2";
import {createServiceListPromise} from "./v2-sources";
import system_roleHtml from './system-role.html?raw';

export let template = system_roleHtml;

export type TSystemPartial = Pick<ISystem, 'id' | 'name' | 'definitionreadonly'>
export type TSystemRolePartial = Pick<ISystemRole, 'id' | 'name' | 'roleenum'>

export interface ICustomSystemAccess {
    id: number
    system: TSystemPartial
    role: TSystemRolePartial | null
    name: string
    name_full: string
    original_name: string
    tooltip: string
    type: 'system' | 'role'

}

export interface IComboboxOptions_SystemRole {
    comboboxOptions?: Partial<IComboboxV2Options<ICustomSystemAccess>>
    showIcon?: boolean
    /**
     * When true, request searchsystem with skipsystemavailableto so systems the
     * caller cannot assign still appear (user-search filter). Role AvailableTo stays on.
     */
    skipSystemAvailableTo?: boolean
}

export class viewModel {
    /** Merged options object shared with v2 (reload / isReady written onto this). */
    private readonly v2Options: IComboboxV2Options<ICustomSystemAccess>;
    /**
     * Shared with v2. Non-empty text ⇒ list labels use `name_full` (aligned with
     * {@link IComboboxV2Options.inputAfterSelect}); empty ⇒ short `name` in the open list.
     */
    private readonly input: KnockoutObservable<string>;
    private readonly showIcon: boolean;

    constructor(private params: IComboboxOptions_SystemRole) {
        this.showIcon = params.showIcon === true
        const callerOptions = params.comboboxOptions ?? {}
        // Own the input (or reuse the caller's) so label can read it without waiting on v2 writeback.
        this.input = callerOptions.input = callerOptions.input ?? ko.observable('')
        this.v2Options = _.defaultsDeep(callerOptions, this.getComboboxParams()) as IComboboxV2Options<ICustomSystemAccess>
        if (this.v2Options.data != null) {
            delete this.v2Options.promise
        }
    }

    private getComboboxParams(): IComboboxV2Options<ICustomSystemAccess> {
        const sort: ((o: ICustomSystemAccess) => unknown)[] = []
        if (global.permissionValidator.superAdmin()) {
            sort.push((o: ICustomSystemAccess) => o.system.name != SYSTEM_MEGA_EADM)
        }
        sort.push((o: ICustomSystemAccess) => o.system.name.toLowerCase())
        sort.push((o: ICustomSystemAccess) => {
            if (o.system.name == SYSTEM_MEGA_EADM) {
                return o.role?.roleenum ?? 0
            }
            return o.role?.name.toLowerCase() ?? ''
        })
        const listSource = createServiceListPromise<ISystem>({
            name: 'searchsystem',
            params: getSearchParamsFn('searchsystem')({
                resultsprpage: MAX_RESULTS_PER_PAGE,
                ...(this.params.skipSystemAvailableTo ? {skipsystemavailableto: true} : {}),
            }),
        })
        return {
            input: this.input,
            prepareData: {
                reduce: (arr: ISystem[]) => arr.reduce((acc: ICustomSystemAccess[], system: ISystem) => {
                    const s: TSystemPartial = _.pick(system, 'id', 'name', 'definitionreadonly')
                    acc.push({
                        id: s.id,
                        name: s.name,
                        name_full: s.name,
                        original_name: system.name,
                        role: null,
                        system: s,
                        tooltip: lm.t(nsTooltip.getTitle_system(system) ?? '') ?? '',
                        type: 'system',
                    })
                    // Same as permissions-v2 availableRolesForSystem: never offer Mega SuperAdmin.
                    const roles = system.name == SYSTEM_MEGA_EADM
                        ? system.roles.filter(role => role.roleenum < Permission.SuperAdmin)
                        : system.roles;
                    acc.push(...roles.map(role => {
                        const name = getRoleName(system, role) ?? '';
                        const r: TSystemRolePartial = _.extend({name}, _.pick(role, 'id', 'roleenum'))
                        const ret: ICustomSystemAccess = {
                            id: r.id,
                            name: name,
                            name_full: `${s.name} ${HIERARCHY_ARROW} ${r.name}`,
                            original_name: role.name,
                            role: r,
                            system: system,
                            tooltip: lm.t(nsTooltip.getTitle_role(system, role) ?? '') ?? '',
                            type: 'role',
                        }
                        return ret
                    }))
                    return acc
                }, [])
            },
            wrapperClass: 'system-access-combobox',
            inputAfterSelect: (o) => o.name_full,
            option: {
                dataType: o => o.type,
                sortBy: sort,
                label: (o) => this.getString(o, this.input() ?? '') ?? '',
                tooltip: (o) => o?.tooltip ?? '',
            },
            promise: listSource.promise as unknown as () => Promise<ICustomSystemAccess[]>,
            onDispose: listSource.onDispose,
            reloadWhen: lm.translations,
        };
    }

    private getString(o: ICustomSystemAccess, s: string) {
        return s ? o.name_full: o.name
    }

    dispose() {
        this.v2Options.onDispose?.()
    }
}
